import { useEffect, useState } from "react";
import { ModalControlData, useModalControl, useRequest } from "../hooks/commonHooks";
import { AnalyzedSummary, AnalyzedUnit, AnalyzedUnitConfig, BatchUpdateStatusData, IssueOverview } from "../models/analyzed_unit_model";
import { Button, Checkbox, Dropdown, Form, FormProps, Modal, Table, TextArea } from "semantic-ui-react";
import { fieldError, formatDateTime } from "../utils";
import { restRequest } from "../http";
import { useFormData } from "../hooks/formHooks";
import _, { result, set } from "lodash";
import { dialogControl } from "../controls/dialog-control";
import { notificationControl } from "../controls/notification-control";

export function BatchUpdateStatus({ resultId, modalControl, onFinish }: { resultId: number, modalControl: ModalControlData<number[]>, onFinish: () => void }) {

    const [summaries, setSummaries] = useState<AnalyzedSummary[]>([]);

    const [issueOverviewList, setIssueOverviewList] = useState<IssueOverview[]>([]);
    const [searchOverviewKey, setSearchOverviewKey] = useState('');

    const [data, setField, setData] = useFormData<BatchUpdateStatusData>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);

    const [selectedFileIds, setSelectedFileIds] = useState<{ [fileId: number]: boolean }>({});

    const [product, setProduct] = useState('');
    const [order, setOrder] = useState('');
    const [testingStation, setTestingStation] = useState('');


    useEffect(() => {
        setLoading(true);
        restRequest<{ files: AnalyzedSummary[], product: string, order: string, testing_station: string }>(
            "analyzed_unit/find_infos_for_batch_update_status", { result_id: resultId, file_ids: modalControl.data }, 'post'
        ).then(info => {
            setSummaries(info.files);
            setProduct(info.product);
            setOrder(info.order);
            setTestingStation(info.testing_station);
        }).finally(() => setLoading(false));
    }, [resultId, JSON.stringify(modalControl.data)])


    useEffect(() => {
        if (!product) {
            return;
        }

        restRequest<IssueOverview[]>(
            `analyzed_unit/search_issue_overviews`, { product, name: searchOverviewKey }, 'post'
        ).then(setIssueOverviewList)
    }, [searchOverviewKey, product]);

    const [config] = useRequest<AnalyzedUnitConfig>("analyzed_unit/config", null, 'get')


    const onSubmit = (
        event: React.FormEvent<HTMLFormElement>,
        formProps: FormProps
    ) => {
        event.preventDefault();

        setLoading(true);

        let url: string;
        let formData = {
            ...data,
            result_id: resultId,
            file_ids: _.map(selectedFileIds, (v, k) => v ? Number(k) : null).filter(v => v)
        };

        restRequest<number>('analyzed_unit/batch_update_status', formData, "post")
            .then(
                (result) => {
                    onFinish();
                    setData({} as any);
                    setInvalidError({});

                    if (result > 0) {
                        dialogControl.alert('Some sequences\' statuses had already updated and it couldn\'t be updated again because the current user don\'t have analyzed unit\'s update permission.')
                    } else {
                        notificationControl.showSuccess('Update Status Successfully!');
                    }
                },
                (resp) => {
                    if (resp.status === 2) {
                        setInvalidError(resp.data);
                    }
                }
            )
            .finally(() => setLoading(false));
    };

    return <>
        <Modal {...modalControl.props} onClose={() => {
            modalControl.setOpen(false);
        }} size="fullscreen">
            <Modal.Header>Batch Update Status for {product}/{order}/{testingStation} {modalControl.props.open ? 'ooo' : 'ccccccccc'}</Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>
                    <Form.Group widths="equal">
                        <Table>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell><Checkbox onClick={(e, d) => {
                                        setSelectedFileIds(d.checked ? summaries.reduce((acc, s) => {
                                            acc[s.file_id] = true;
                                            return acc;
                                        }, {} as { [fileId: number]: boolean }) : {});
                                    }} /></Table.HeaderCell>
                                    <Table.HeaderCell>Serial</Table.HeaderCell>
                                    <Table.HeaderCell>Date</Table.HeaderCell>
                                    <Table.HeaderCell>Sequence Status</Table.HeaderCell>
                                    <Table.HeaderCell>Fail Measurements</Table.HeaderCell>
                                    <Table.HeaderCell>Update Status</Table.HeaderCell>
                                    <Table.HeaderCell></Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {summaries?.map((s, i) => {
                                    return (
                                        <Table.Row key={s.file_id}>
                                            <Table.Cell>
                                                <Checkbox checked={selectedFileIds[s.file_id] || false} onClick={(e, d) => {
                                                    setSelectedFileIds({ ...selectedFileIds, [s.file_id]:  d.checked as boolean });
                                                }}
                                                /></Table.Cell>
                                            <Table.Cell>{s.serial_number}</Table.Cell>
                                            <Table.Cell>{formatDateTime(s.date, true)}</Table.Cell>
                                            <Table.Cell>
                                                {
                                                    ["Unknown", "Pass", "Fail", "Invalid", "Stopped"][
                                                    s.status
                                                    ]
                                                }
                                            </Table.Cell>
                                            <Table.Cell>{s.fail_measurements}</Table.Cell>
                                            <Table.Cell>
                                                {config?.AVAILABLE_UPDATE_STATUSES &&
                                                    config.AVAILABLE_UPDATE_STATUSES[
                                                    s.update_status === s.status ? 0 : s.update_status
                                                    ]}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Button
                                                    size="mini"
                                                    target="_blank"
                                                    as="a"
                                                    href={`/testing_file_visual/${s.file_id}`}
                                                    icon="search"
                                                ></Button>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input
                            label="Issue Name"
                            name="issue_overview_id"
                            error={fieldError("issue_overview_id", invalidError)}
                        >
                            <Dropdown
                                name="issue_overview_id"
                                value={data.issue_overview_id || 0}
                                onChange={setField}
                                options={[{ text: "None", value: 0, key: 0 }].concat(issueOverviewList.map((p, i) => ({ text: p.name, value: p.id, key: p.id })))}
                                onSearchChange={(e, d) => {
                                    setSearchOverviewKey(d.value as string);
                                }}

                                search
                                selection
                                fluid
                            ></Dropdown>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Update Status">
                            <Dropdown
                                name="update_status"
                                value={data.update_status || 0}
                                onChange={setField}
                                options={(config?.AVAILABLE_UPDATE_STATUSES || []).map(
                                    (p, i) => ({ text: p, value: i, key: i })
                                )}
                                search
                                selection
                                fluid
                            ></Dropdown>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Remark">
                            <TextArea
                                name="remark"
                                value={data.remark || ""}
                                onChange={setField}
                            />
                        </Form.Input>
                    </Form.Group>

                    <div>
                        <Button primary type="submit">Save</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button
                            as="a"
                            onClick={(e) => {
                                modalControl.setOpen(false);
                            }}
                        >
                            Close
                        </Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal>

    </>
}